/**
 * Topic Pulse surfaces (NEWS-453; kerf UI, NM-NWFSDV).
 *
 * - **Rail marks** — `barGraphJsx` (the rail row's 7-day bars) and
 *   `sparklineJsx` (the compact surfaces' trend line). Both are app-owned
 *   artwork, so their CSS stays app CSS (`.bargraph*`, `.sparkline`).
 * - **Compact topic pulse** — a framed kerf UI `ContentItem` above the feed for
 *   exactly one soloed topic.
 * - **Section rollup** — a framed `ContentItem` strip under the filter bar.
 * - **Drill-down** — a kerf UI `DialogSurface`, a native `<dialog>`
 *   (`data-dialog="pulse"`, the same frame as `appDialog`): a `SegmentedControl`
 *   for the 7/30/90-day period, `ValueTable`s for the facts, `FramedList`s for
 *   sources and threads, and the daily chart (app-owned artwork) with its
 *   accessible data table behind a kerf UI `Disclosure`.
 *
 * Rendering only — every `data-*` click is handled by `delegate()` in `app.tsx`.
 */

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { px } from '@kerfjs/ui/css-values';
import { deviceClass } from '@kerfjs/ui/device-class';
import { Disclosure } from '@kerfjs/ui/disclosure';
import { FramedList, FramedListRow } from '@kerfjs/ui/framed-list';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { Row } from '@kerfjs/ui/row';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import type { SafeHtml } from 'kerfjs';

import type { PulseResp } from '../../../api/schemas.js';
import { icon } from '../../components/icons.js';

function lineCoords(values: readonly number[], width: number, height: number): { x: number; y: number }[] {
  if (values.length === 0) return [];
  const max = Math.max(1, ...values);
  return values.map((value, index) => {
    const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
    const y = height - (value / max) * (height - 3) - 1.5;
    return { x, y };
  });
}

/**
 * Tiny comparison chart shared by the compact pulse surfaces.
 *
 * It carries a **baseline** and a dot on the latest value (NM-141) so it reads
 * as a trend with a floor rather than a stray diagonal, and both an `aria-label`
 * (for a screen reader) and a matching `title` (a desktop hover tooltip) so its
 * meaning — the metric and window — is discoverable without one. Keep the caller's
 * `label` naming both, e.g. "daily stories, last 7 days".
 */
export function sparklineJsx(values: readonly number[], label: string): SafeHtml {
  const w = 72;
  const h = 24;
  const coords = lineCoords(values, w, h);
  const points = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const last = coords.at(-1);
  return (
    <span class="sparkline" role="img" aria-label={label} title={label}>
      <svg viewBox="0 0 72 24" width="72" height="24" aria-hidden="true">
        {/* A faint floor so the mark reads as a chart, not a diagonal (NM-141). */}
        <line
          class="spark-base"
          x1="0"
          y1={String(h - 1.5)}
          x2={String(w)}
          y2={String(h - 1.5)}
          vector-effect="non-scaling-stroke"
        />
        <polyline points={points} fill="none" vector-effect="non-scaling-stroke" />
        {/* A dot on the most recent day anchors "the trend ends here". */}
        {last === undefined ? '' : <circle class="spark-dot" cx={last.x.toFixed(1)} cy={last.y.toFixed(1)} r="1.7" />}
      </svg>
    </span>
  );
}

/**
 * A tiny **bar** chart for the rail row (NM-229) — one bar per day, replacing the
 * sparkline so the activity mark sits in the flow beside the topic's tags rather
 * than as a pinned line. Each bar's height is its share of the window max; a
 * zero/low day keeps a short stub so the shape reads as a chart with a floor, not
 * gaps. `aria-label` + `title` carry the metric + window like the sparkline did.
 */
export function barGraphJsx(values: readonly number[], label: string): SafeHtml {
  const max = Math.max(1, ...values);
  return (
    <span class="bargraph" role="img" aria-label={label} title={label}>
      {values.map((value) => (
        <span class="bargraph-bar" style={`height:${String(Math.max(14, Math.round((value / max) * 100)))}%`}></span>
      ))}
    </span>
  );
}

function trendText(pulse: PulseResp): string {
  if (pulse.trendPercent === null) return pulse.storyCount === 0 ? 'No activity' : 'New activity';
  if (pulse.trendPercent === 0) return 'Even with the previous period';
  return `${pulse.trendPercent > 0 ? '+' : ''}${String(pulse.trendPercent)}% vs previous ${String(pulse.days)} days`;
}

function sourceShare(pulse: PulseResp): string {
  return pulse.topSourceShare === null ? '—' : `${String(Math.round(pulse.topSourceShare * 100))}%`;
}

function topSourceLabel(pulse: PulseResp): string {
  return pulse.sources[0]?.label ?? 'Top source';
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

function pulseScopeAttrs(pulse: PulseResp): Record<`data-${string}`, string> {
  return {
    'data-open-pulse-kind': pulse.scope.kind,
    'data-open-pulse-id': pulse.scope.id,
    'data-open-pulse-subcategory': pulse.scope.subcategory ?? '',
  };
}

/** One compact figure: the number in the body weight, its noun quiet beside it. */
function metric(value: string, label: string): SafeHtml {
  return (
    <Text variant="span" size="compact" wrap="nowrap" data-pulse-metric="">
      <strong>{value}</strong> {label}
    </Text>
  );
}

/** The shallow summary shown above the feed for exactly one soloed topic. */
export function compactTopicPulseJsx(pulse: PulseResp): SafeHtml {
  const trend = trendText(pulse);
  return (
    <ContentItem
      frame="framed"
      appearance="surface"
      ariaLabel={`${pulse.scope.label} Pulse`}
      rootAttributes={{ 'data-compact-pulse': '' }}
    >
      <List gap="xs">
        <Row gap="xs" vAlign="middle">
          <Text variant="h2" size="large" wrap="anywhere" data-compact-pulse-title="">
            {pulse.scope.label}
          </Text>
          <Badge size="compact" tone="info">
            Solo
          </Badge>
          <Spacer flex />
          <Button size="compact" emphasis="plain" action="exit-solo-pulse">
            Exit Solo
          </Button>
        </Row>
        <Row gap="m" vAlign="bottom" wrap>
          <List gap="2xs" flex>
            <Text tone="quiet" size="compact">
              30-Day Pulse
            </Text>
            <Row gap={{ column: 'm', row: '2xs' }} wrap controlInsets="rl">
              {metric(String(pulse.storyCount), pulse.storyCount === 1 ? 'story' : 'stories')}
              {metric(String(pulse.activeThreads), pulse.activeThreads === 1 ? 'active thread' : 'active threads')}
              {metric(String(pulse.distinctOutlets), pulse.distinctOutlets === 1 ? 'outlet' : 'outlets')}
              {metric(sourceShare(pulse), topSourceLabel(pulse))}
            </Row>
            <Row gap="xs" vAlign="middle" controlInsets="rl">
              {sparklineJsx(
                pulse.series.map((point) => point.stories),
                `${trend}; daily story volume`,
              )}
              <Text variant="span" tone="quiet" size="compact">
                {trend}
              </Text>
            </Row>
            <Text tone="quiet" size="compact">
              {`Calculated from stored stories${pulse.smallSample ? ' · Small sample' : ''}`}
            </Text>
          </List>
          {/* Text-only (FR-37.4): no icon whose alignment can drift as it wraps. */}
          <Row controlInsets="rl">
            <Button tone="brand" rootAttributes={pulseScopeAttrs(pulse)}>
              Explore Topic Pulse
            </Button>
          </Row>
        </Row>
      </List>
    </ContentItem>
  );
}

/** Understated rollup beneath an active category/subcategory filter. */
export function categoryPulseJsx(pulse: PulseResp): SafeHtml {
  const trend = trendText(pulse);
  return (
    <ContentItem frame="framed" rootAttributes={{ 'data-pulse-rollup': '' }}>
      <Row gap={{ column: 'xs', row: '2xs' }} vAlign="middle" wrap>
        <Text variant="span" size="compact" wrap="nowrap">
          <strong>{pulse.scope.label}</strong>
        </Text>
        {/* FR-37.5: the count and the window never wrap internally. */}
        <Text variant="span" tone="quiet" size="compact" wrap="nowrap" data-pulse-rollup-count="">
          {plural(pulse.storyCount, 'story', 'stories')}
        </Text>
        <Text variant="span" tone="quiet" size="compact" aria-hidden="true">
          ·
        </Text>
        <Text variant="span" tone="quiet" size="compact" wrap="nowrap" data-pulse-rollup-window="">
          last 30 days
        </Text>
        <Text variant="span" tone="quiet" size="compact" wrap="nowrap">
          {trend}
        </Text>
        {sparklineJsx(
          pulse.series.map((point) => point.stories),
          `${pulse.scope.label}: ${trend}`,
        )}
        <Spacer flex />
        <Button size="compact" emphasis="plain" rootAttributes={pulseScopeAttrs(pulse)}>
          Explore Pulse
        </Button>
      </Row>
    </ContentItem>
  );
}

function shortDate(iso: string): string {
  const parsed = new Date(`${iso}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function cadenceText(pulse: PulseResp): string {
  if (pulse.cadence.averageDays === null) return 'Not enough stories to calculate';
  if (pulse.cadence.averageDays < 1) return 'More than one new story a day';
  return `A new story every ${String(pulse.cadence.averageDays)} days`;
}

/** The analysis periods (FR-37.6), as `SegmentedControl` choices. */
const PERIODS = ['7', '30', '90'] as const;

/** A section heading inside the drill-down — a level-3 heading under the dialog title. */
function section(label: string): SafeHtml {
  return <ListHeader label={label} size="xs" headingLevel={3} />;
}

/** The figures, read-only label/value facts: what was covered, and how often. */
function overviewJsx(pulse: PulseResp): SafeHtml {
  return (
    <List gap="xs" rootAttributes={{ 'data-pulse-overview': '' }}>
      {section('Overview')}
      <ValueTable label="Overview" density="compact">
        <ValueTableRow label="Stories" value={String(pulse.storyCount)} />
        <ValueTableRow label="Active Threads" value={String(pulse.activeThreads)} />
        <ValueTableRow label="Distinct Outlets" value={String(pulse.distinctOutlets)} />
        <ValueTableRow
          label="Top Source"
          value={pulse.topSourceShare === null ? '—' : `${topSourceLabel(pulse)} · ${sourceShare(pulse)}`}
        />
      </ValueTable>
      {section('Coverage Cadence')}
      <ValueTable label="Coverage Cadence" density="compact">
        <ValueTableRow label="Typical Pace" value={cadenceText(pulse)} />
        <ValueTableRow label="Longest Quiet Period" value={plural(pulse.cadence.longestQuietDays, 'day', 'days')} />
        <ValueTableRow
          label="Most Active Day"
          value={
            pulse.cadence.mostActiveDate === null
              ? '—'
              : `${shortDate(pulse.cadence.mostActiveDate)} · ${plural(pulse.cadence.mostActiveCount, 'story', 'stories')}`
          }
        />
      </ValueTable>
    </List>
  );
}

/**
 * Daily new stories and thread updates: the chart is app-owned artwork
 * (`.pulse-bars`), and its textual equivalent (FR-37.8) is a real data table
 * behind a disclosure.
 */
function overTimeJsx(pulse: PulseResp): SafeHtml {
  const max = Math.max(1, ...pulse.series.map((candidate) => candidate.stories));
  return (
    <List gap="xs" rootAttributes={{ 'data-pulse-over-time': '' }}>
      {section('Coverage Over Time')}
      <div class="pulse-chart">
        <div class="pulse-bars" role="img" aria-label="Daily new stories and thread updates">
          {pulse.series.map((point) => (
            <span
              class="pulse-day"
              title={`${shortDate(point.date)}: ${String(point.stories)} stories, ${String(point.updates)} updates`}
            >
              <span class="pulse-update" style={`height:${String((point.updates / max) * 100)}%`}></span>
              <span class="pulse-story" style={`height:${String((point.stories / max) * 100)}%`}></span>
            </span>
          ))}
        </div>
        <div class="pulse-legend" aria-hidden="true">
          <span>Stories</span>
          <span>Thread updates</span>
        </div>
      </div>
      <Disclosure title="View Data Table" appearance="framed" rootAttributes={{ 'data-pulse-table': '' }}>
        <table class="pulse-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Stories</th>
              <th scope="col">Updates</th>
            </tr>
          </thead>
          <tbody>
            {pulse.series.map((point) => (
              <tr>
                <td>{shortDate(point.date)}</td>
                <td>{String(point.stories)}</td>
                <td>{String(point.updates)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Disclosure>
    </List>
  );
}

/** Primary-source concentration (FR-37.10), each share drawn as a bar. */
function sourcesJsx(pulse: PulseResp): SafeHtml {
  return (
    <List gap="xs" rootAttributes={{ 'data-pulse-sources': '' }}>
      {section('Source Concentration')}
      {pulse.sources.length === 0 ? (
        <Text tone="quiet" size="compact">
          No source metadata in this period.
        </Text>
      ) : (
        <FramedList label="Source Concentration">
          {pulse.sources.map((source) => (
            <FramedListRow>
              <List gap="2xs">
                <Row gap="xs" vAlign="middle">
                  <Text variant="span" size="compact" wrap="truncate">
                    {source.label}
                  </Text>
                  <Spacer flex />
                  <Text variant="span" tone="quiet" size="compact" wrap="nowrap">
                    {`${String(source.count)} · ${String(Math.round(source.share * 100))}%`}
                  </Text>
                </Row>
                <span class="source-bar" aria-hidden="true">
                  <i style={`width:${String(source.share * 100)}%`}></i>
                </span>
              </List>
            </FramedListRow>
          ))}
          {pulse.otherSourceCount > 0 ? (
            <FramedListRow>
              <Row gap="xs" vAlign="middle">
                <Text variant="span" size="compact">
                  Other outlets
                </Text>
                <Spacer flex />
                <Text variant="span" tone="quiet" size="compact" wrap="nowrap">
                  {String(pulse.otherSourceCount)}
                </Text>
              </Row>
            </FramedListRow>
          ) : null}
        </FramedList>
      )}
      <Text tone="quiet" size="compact">
        Counts each story’s primary source, not viewpoints or independent reporting.
      </Text>
    </List>
  );
}

/** The three most-updated threads (FR-37.12). */
function threadsJsx(pulse: PulseResp): SafeHtml {
  return (
    <List gap="xs" rootAttributes={{ 'data-pulse-threads': '' }}>
      {section('Most-Updated Threads')}
      {pulse.threads.length === 0 ? (
        <Text tone="quiet" size="compact">
          No developing threads in this period.
        </Text>
      ) : (
        <FramedList label="Most-Updated Threads">
          {pulse.threads.map((thread) => (
            <FramedListRow>
              <Row gap="xs" vAlign="top">
                <List gap="2xs" flex>
                  <Text variant="span" size="compact">
                    <strong>{thread.title}</strong>
                  </Text>
                  <Text variant="span" tone="quiet" size="compact">
                    {`${new Date(thread.startedAt).toLocaleDateString()}–${new Date(thread.latestAt).toLocaleDateString()}`}
                  </Text>
                </List>
                <Badge size="compact" tone="info">
                  {plural(thread.updates, 'update', 'updates')}
                </Badge>
              </Row>
            </FramedListRow>
          ))}
        </FramedList>
      )}
    </List>
  );
}

/** The loading body: the overview's shape as unanimated placeholders. */
function loadingJsx(): SafeHtml {
  return (
    <List gap="xs">
      <Text tone="quiet" size="compact" align="center">
        Calculating from stored stories…
      </Text>
      <ValueTable label="Overview" density="compact">
        <ValueTableRow label="Stories" value="" placeholder />
        <ValueTableRow label="Active Threads" value="" placeholder />
        <ValueTableRow label="Distinct Outlets" value="" placeholder />
        <ValueTableRow label="Top Source" value="" placeholder />
      </ValueTable>
    </List>
  );
}

/** The viewport's device class (kerf UI), read during render so the presentation follows it. */
const device = deviceClass();

/**
 * Full deterministic drill-down (FR-37.6–37.8), opened deliberately from a
 * compact surface. kerf UI owns the modal behaviour (top layer, inert page,
 * Escape, the header close button, light dismiss); the store owns
 * `pulseDetailOpen`, and the dialog `close` listener in `app.tsx` clears it.
 */
export function pulseDialogJsx(pulse: PulseResp | null, loading: boolean, days: 7 | 30 | 90): SafeHtml {
  const kindLabel = pulse?.scope.kind === 'category' ? 'Section Pulse' : 'Topic Pulse';
  return (
    <DialogSurface
      label={pulse?.scope.label ?? 'Loading Pulse…'}
      rootAttributes={{ 'data-key': 'dialog-pulse', 'data-dialog': 'pulse' }}
      size="large"
      maxHeight="viewport"
      presentation={device.value.handset ? 'fullscreen' : 'modal'}
      lightDismiss
    >
      <List gap="m" rootAttributes={{ 'data-dialog-body': '' }}>
        <Row gap="xs" vAlign="middle" wrap controlInsets="rl">
          <Text variant="span" tone="quiet" size="compact">
            {kindLabel}
          </Text>
          <Spacer flex />
          <SegmentedControl
            id="pulse-period"
            label="Analysis Period"
            action="pulse-days"
            size="small"
            value={String(days) as (typeof PERIODS)[number]}
            choices={PERIODS.map((period) => ({ value: period, label: `${period} Days`, disabled: loading }))}
          />
        </Row>
        <div data-pulse-content="" aria-busy={loading ? 'true' : 'false'}>
          {pulse === null ? (
            loadingJsx()
          ) : (
            <List gap="m">
              {pulse.smallSample ? (
                <StateBanner
                  tone="warning"
                  icon={icon('warn')}
                  title="Small Sample"
                  detail={`Trends may shift quickly. ${trendText(pulse)}.`}
                />
              ) : (
                <Text tone="quiet" size="compact" data-pulse-trend="">
                  {`${trendText(pulse)}.`}
                </Text>
              )}
              <Grid minColumnWidth={px(300)} gap="l">
                {overviewJsx(pulse)}
                {overTimeJsx(pulse)}
                {sourcesJsx(pulse)}
                {threadsJsx(pulse)}
              </Grid>
              <Text tone="quiet" size="compact" data-pulse-method="">
                All figures are calculated from stories stored in Newsmonger. No AI analysis.
              </Text>
            </List>
          )}
        </div>
      </List>
    </DialogSurface>
  );
}
